// Shared primitives used across PaperPilot screens.
// PaperCard, ScoreGauge, Sparkline, KPITile, etc.

// Tiny circular score gauge — used heavily.
function ScoreArc({ value = 0.8, color = 'var(--acc-indigo)', size = 32, stroke = 2.5 }) {
  const r = (size - stroke) / 2;
  const c = Math.PI * 2 * r;
  const off = c * (1 - value);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        style={{ filter: `drop-shadow(0 0 4px ${color === 'var(--acc-indigo)' ? 'rgba(136,150,255,0.5)' : color})` }}/>
    </svg>
  );
}

// Score with label below — compact triad widget
function Gauge({ value, label, color }) {
  const v = Math.round(value * 100);
  return (
    <div className="gauge">
      <div className="arc" style={{ position: 'relative', width: 32, height: 32 }}>
        <ScoreArc value={value} color={color} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 500 }}>{v}</div>
      </div>
      <div className="lbl">{label}</div>
    </div>
  );
}

// Linear score bar (used in detail page evaluations)
function ScoreBar({ value = 0.7, color = 'var(--acc-indigo)', height = 4 }) {
  return (
    <div style={{
      height, borderRadius: 999, background: 'rgba(255,255,255,0.06)',
      overflow: 'hidden', position: 'relative',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        width: `${value * 100}%`,
        background: `linear-gradient(90deg, ${color}66, ${color})`,
        boxShadow: `0 0 8px ${color}88`,
        borderRadius: 999,
      }}/>
    </div>
  );
}

// Sparkline SVG path
function Sparkline({ data, width = 80, height = 24, color = 'var(--acc-indigo)', fill = true, stroke = 1.5 }) {
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  const pts = data.map((v, i) => [i * step, height - ((v - min) / span) * (height - 2) - 1]);
  const path = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const fillPath = path + ` L ${width} ${height} L 0 ${height} Z`;
  const gid = 'sg' + Math.random().toString(36).slice(2, 7);
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {fill && <path d={fillPath} fill={`url(#${gid})`}/>}
      <path d={path} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// KPI tile — used on dashboard
function KPI({ label, value, delta, deltaKind = 'up', spark, icon, accent = 'var(--acc-indigo)' }) {
  const deltaColor = deltaKind === 'up' ? 'var(--ok)' : deltaKind === 'down' ? 'var(--danger)' : 'var(--fg-3)';
  return (
    <div className="card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 86, position: 'relative', overflow: 'hidden' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="row gap-2" style={{ color: 'var(--fg-2)' }}>
          {icon && <I name={icon} size={13}/>}
          <span style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
        </div>
        {delta && (
          <div className="mono" style={{ fontSize: 10.5, color: deltaColor, display: 'flex', alignItems: 'center', gap: 2 }}>
            {deltaKind === 'up' ? '↑' : deltaKind === 'down' ? '↓' : '·'} {delta}
          </div>
        )}
      </div>
      <div className="row" style={{ alignItems: 'baseline', gap: 8, marginTop: 'auto' }}>
        <div style={{ fontSize: 26, fontWeight: 500, fontFamily: 'var(--font-sans)', letterSpacing: '-0.02em', color: 'var(--fg-0)', whiteSpace: 'nowrap', overflowWrap: 'normal', wordBreak: 'normal' }}>{value}</div>
        {spark && <div style={{ marginLeft: 'auto', flexShrink: 0 }}><Sparkline data={spark} width={56} height={20} color={accent}/></div>}
      </div>
    </div>
  );
}

// Avatar group for author lists
function Avatars({ names = [], size = 18 }) {
  const initials = (n) => n.split(' ').map(p => p[0]).slice(0, 2).join('');
  const palette = ['#5B68E0','#6AD7F0','#B49BFF','#E8B86A','#6BD8A7','#E58C7A'];
  return (
    <div style={{ display: 'flex' }}>
      {names.slice(0, 4).map((n, i) => (
        <div key={i} style={{
          width: size, height: size, borderRadius: 999,
          background: palette[i % palette.length], color: '#0B0D12',
          fontSize: size * 0.42, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginLeft: i ? -6 : 0, border: '1.5px solid var(--bg-2)',
        }}>{initials(n)}</div>
      ))}
      {names.length > 4 && (
        <div style={{ width: size, height: size, borderRadius: 999, background: 'var(--bg-4)', color: 'var(--fg-1)', fontSize: 9, fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: -6, border: '1.5px solid var(--bg-2)' }}>+{names.length - 4}</div>
      )}
    </div>
  );
}

// Compact paper card — used in dashboard, explore, library, project boards.
function PaperCard({ paper, dense = false, showWhy = true, showActions = true }) {
  const { t } = useT();
  const nav = React.useContext(window.NavContext || React.createContext({}));
  const codeBadge = paper.code === 'released' ? <span className="chip ok"><I name="github" size={10}/> {t('pc.code')}</span> :
                    paper.code === 'pending'  ? <span className="chip ghost"><I name="github" size={10}/> {t('pc.code.soon')}</span> : null;
  return (
    <div className="pp-paper-card" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)} style={{ cursor: 'pointer' }}>
      <div className="pp-paper-head">
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <span className="chip accent">{paper.venue}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{paper.date}</span>
          {paper.arxiv && <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>· arXiv {paper.arxiv}</span>}
          {paper.must && <span className="chip violet" style={{ marginLeft: 'auto' }}><I name="flame" size={10}/> {t('pc.must')}</span>}
        </div>
        <div className="serif pp-paper-title" style={{ fontSize: 17.5, lineHeight: 1.18, color: 'var(--fg-0)' }}>{paper.title}</div>
        <div className="row gap-2" style={{ marginTop: 2 }}>
          <Avatars names={paper.authors} size={16}/>
          <span style={{ fontSize: 11.5, color: 'var(--fg-2)' }} className="ellipsis">
            {paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ` +${paper.authors.length - 3}` : ''}
          </span>
        </div>
      </div>

      {!dense && paper.summary && (
        <div style={{ fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.55, marginTop: 8 }}>{paper.summary}</div>
      )}

      <div className="row gap-2" style={{ flexWrap: 'wrap', marginTop: 10 }}>
        {paper.tags?.map(t => <span key={t} className="tag indigo">{t}</span>)}
        {paper.dataset && <span className="tag">{paper.dataset}</span>}
        {codeBadge}
      </div>

      {showWhy && paper.why && (
        <div className="pp-paper-why">
          <I name="sparkles" size={11} style={{ color: 'var(--acc-violet)', marginTop: 2 }}/>
          <div>
            <span style={{ color: 'var(--acc-violet)', fontSize: 10.5, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 6 }}>{t('pc.why')}</span>
            <span style={{ color: 'var(--fg-1)', fontSize: 12 }}>{paper.why}</span>
          </div>
        </div>
      )}

      <div className="pp-paper-foot">
        <div className="row gap-3">
          <ScoreInline label={t('pc.score.worth')} value={paper.worth   ?? 0.86} color="var(--acc-indigo)"/>
          <ScoreInline label={t('pc.score.novel')} value={paper.novel   ?? 0.78} color="var(--acc-violet)"/>
          <ScoreInline label={t('pc.score.impl')}  value={paper.impl    ?? 0.62} color="var(--acc-cyan)"/>
        </div>
        {showActions && (
          <div className="row gap-1" style={{ marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="iconbtn" title={t('pc.action.bookmark')}><I name="bookmark" size={14}/></button>
            <button className="iconbtn" title={t('pc.action.addproject')}><I name="plus" size={14}/></button>
            <button className="iconbtn" title={t('pc.action.compare')}><I name="compare" size={14}/></button>
            <button className="iconbtn" title={t('pc.action.openpdf')} onClick={() => nav.navigate && nav.navigate('reader')}><I name="external" size={14}/></button>
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreInline({ label, value, color }) {
  const v = Math.round(value * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 22, height: 22, position: 'relative' }}>
        <ScoreArc value={value} color={color} size={22} stroke={2}/>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 8.5, fontWeight: 500, color: 'var(--fg-0)' }}>{v}</div>
      </div>
      <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)', letterSpacing: '0.08em' }}>{label}</span>
    </div>
  );
}

// Section heading with kicker number, optional action
function SectionHead({ kicker, title, sub, action }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', padding: '0 0 10px' }}>
      <div className="col gap-1">
        <div className="section-title">
          {kicker && <span className="num">{kicker}</span>}
          <span>{title}</span>
        </div>
        {sub && <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

// Inject paper card / shared styles
if (typeof document !== 'undefined' && !document.getElementById('pp-primitives-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-primitives-styles';
  s.textContent = `
  .pp-paper-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    padding: 14px;
    display: flex; flex-direction: column;
    transition: border-color .15s, background .15s;
    position: relative;
  }
  .pp-paper-card:hover {
    border-color: var(--border-3);
    background: var(--bg-3);
  }
  .pp-paper-head { display: flex; flex-direction: column; gap: 6px; }
  .pp-paper-title { font-weight: 500; letter-spacing: -0.012em; }
  .pp-paper-why {
    display: flex; gap: 6px; margin-top: 10px;
    padding: 8px 10px;
    background: linear-gradient(90deg, rgba(180,155,255,0.08), rgba(180,155,255,0.02));
    border-left: 2px solid var(--acc-violet);
    border-radius: 0 6px 6px 0;
  }
  .pp-paper-foot {
    display: flex; align-items: center; gap: 12px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed var(--border-1);
  }
  `;
  document.head.appendChild(s);
}

export { ScoreArc, Gauge, ScoreBar, Sparkline, KPI, Avatars, PaperCard, ScoreInline, SectionHead };
